/**
 * Snakes and Ladders Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → dice roll → move →
 * animation → snake/ladder → turn management → win.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Responsive layout: board + controls.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { SnakesLaddersEngine } from '../SnakesLaddersEngine';
import { SnakesLaddersAI } from '../SnakesLaddersAI';
import { SnakesLaddersBoard } from './SnakesLaddersBoard';
import { SnakesLaddersDice } from './SnakesLaddersDice';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { RoomCodeDisplay } from '../../components/RoomCodeDisplay';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { SnakesLaddersErrorHandler } from '../services/SnakesLaddersErrorHandler';
import { SnakesLaddersStatisticsService } from '../services/SnakesLaddersStatisticsService';
import { SnakesLaddersGamePersistence } from '../services/SnakesLaddersGamePersistence';
import { MultiplayerSnakesLaddersService } from '../services/MultiplayerSnakesLaddersService';
import { WebSocketService } from '../../services/WebSocketService';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { GameResult } from '../../services/multiplayer/GenericMultiplayerService';
import {
  SnakesLaddersGameSettings,
  SnakesLaddersGameState,
  MoveResult,
  PlayerState,
  SnakesLaddersGameProps,
  PLAYER_COLORS,
} from '../types';
import './SnakesLaddersGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const SnakesLaddersGameInner: React.FC<SnakesLaddersGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<SnakesLaddersGameSettings | null>(null);
  const [gameState, setGameState] = useState<SnakesLaddersGameState | null>(null);
  const [diceValue, setDiceValue] = useState<number | null>(null);
  const [rolling, setRolling] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [lastMove, setLastMove] = useState<MoveResult | null>(null);

  // Multiplayer state
  const [multiplayerService] = useState<MultiplayerSnakesLaddersService>(
    () => new MultiplayerSnakesLaddersService(WebSocketService.getInstance())
  );
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');

  // Refs
  const engineRef = useRef<SnakesLaddersEngine>(new SnakesLaddersEngine());
  const aiRef = useRef<SnakesLaddersAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const hasAttemptedJoin = useRef(false);
  const isCreatingRoom = useRef(false);

  // Services (lazy init)
  const errorHandlerRef = useRef<SnakesLaddersErrorHandler | null>(null);
  const statsServiceRef = useRef<SnakesLaddersStatisticsService | null>(null);
  const persistenceRef = useRef<SnakesLaddersGamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new SnakesLaddersErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new SnakesLaddersStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new SnakesLaddersGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize services:', e);
    }

    return () => {
      aiRef.current?.destroy();
    };
  }, []);

  // Check for saved game on mount
  useEffect(() => {
    if (persistenceRef.current?.hasAutoSave()) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Saved Game Found',
        message: 'A saved game was found. Start a new game to continue.',
        duration: 4000,
      });
    }
  }, [addNotification]);

  // =========================================================================
  // Multiplayer event handlers setup
  // =========================================================================
  useEffect(() => {
    multiplayerService.onGameStarted(() => {
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    });

    multiplayerService.onMoveUpdate((move: MoveResult, gameState: any) => {
      if (gameState) {
        setGameState(gameState);
      }
    });

    multiplayerService.onGameOver((result: GameResult) => {
      const message = result.result === 'win'
        ? `🎉 ${result.winner} wins!`
        : 'Game over!';
      NotificationService.getInstance().sendInfoNotification(message);
    });

    multiplayerService.onPlayerDisconnected(() => {
      setOpponentConnected(false);
      NotificationService.getInstance().sendWarningNotification(
        'Opponent disconnected. Waiting for reconnection...'
      );
    });

    multiplayerService.onPlayerConnected(() => {
      setOpponentConnected(true);
      NotificationService.getInstance().sendSuccessNotification('Opponent reconnected!');
    });

    multiplayerService.onGameStateReceived((gameState: any) => {
      if (gameState) {
        setGameState(gameState);
      }
    });

    multiplayerService.onConnectionStatus((status) => {
      setConnectionStatus(status);
      if (status === 'reconnecting') {
        NotificationService.getInstance().sendWarningNotification('Connection lost. Reconnecting...');
      } else if (status === 'connected') {
        NotificationService.getInstance().sendSuccessNotification('Connected!');
      } else if (status === 'disconnected') {
        NotificationService.getInstance().sendErrorNotification('Disconnected from server');
      }
    });

    multiplayerService.onError((error: string) => {
      NotificationService.getInstance().sendErrorNotification(`Multiplayer error: ${error}`);
    });

    multiplayerService.onDiceResult((diceValue: number) => {
      setDiceValue(diceValue);
    });
  }, [multiplayerService]);

  // =========================================================================
  // Deep link room code handling
  // =========================================================================
  useEffect(() => {
    if (initialRoomCode && !roomCode && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;
      setView('playing');
      setRoomCode(initialRoomCode);
      setWaitingForOpponent(true);

      multiplayerService.joinRoom(initialRoomCode)
        .then(() => {
          setWaitingForOpponent(false);
          setOpponentConnected(true);
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Unable to join room ${initialRoomCode}. ${error.message || 'Please check the room code.'}`
          );
          setView('mode-select');
          setWaitingForOpponent(false);
          setRoomCode(null);
        });
    }
  }, [initialRoomCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: SnakesLaddersGameSettings) => {
      // Handle multiplayer room creation
      if (newSettings.mode === 'multiplayer-realtime') {
        if (isCreatingRoom.current) return;
        isCreatingRoom.current = true;

        multiplayerService.createRoom()
          .then((room) => {
            setRoomCode(room.code);
            setWaitingForOpponent(true);
            setSettings(newSettings);
            setView('playing');
            isCreatingRoom.current = false;
          })
          .catch((error: any) => {
            NotificationService.getInstance().sendErrorNotification(
              `Failed to create room: ${error.message || 'Unknown error'}`
            );
            isCreatingRoom.current = false;
          });
        return;
      }

      const engine = engineRef.current;
      engine.initializeGame(newSettings);

      setSettings(newSettings);
      setGameState(engine.getGameState());
      setDiceValue(null);
      setLastMove(null);
      setRolling(false);
      setAnimating(false);
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('snakes-and-ladders');

      // Set up AI if single player
      if (newSettings.mode === 'singleplayer' && newSettings.aiDifficulty) {
        aiRef.current = new SnakesLaddersAI(newSettings.aiDifficulty);
      } else {
        aiRef.current?.destroy();
        aiRef.current = null;
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `${newSettings.mode === 'singleplayer' ? 'Single player' : 'Multiplayer'} game started!`,
        duration: 2000,
      });
    },
    [addNotification, multiplayerService]
  );

  /** Join a private online game room */
  const handleJoinPrivateGame = useCallback(async (code: string) => {
    setRoomCode(code);
    setWaitingForOpponent(true);
    setView('playing');

    try {
      await multiplayerService.joinRoom(code);
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    } catch (error: any) {
      NotificationService.getInstance().sendErrorNotification(
        `Unable to join room ${code}. ${error.message || 'Please check the room code.'}`
      );
      setView('mode-select');
      setWaitingForOpponent(false);
      setRoomCode(null);
    }
  }, [multiplayerService]);

  const handleCancelWaiting = useCallback(() => {
    multiplayerService.leaveGame();
    setWaitingForOpponent(false);
    setRoomCode(null);
    setView('mode-select');
    setSettings(null);
    isCreatingRoom.current = false;
  }, [multiplayerService]);

  const handleRollDice = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver() || rolling || animating) return;

    const currentPlayer = engine.getCurrentPlayer();

    // In single player, only allow human to roll manually
    if (settings?.mode === 'singleplayer' && currentPlayer.type === 'ai') {
      return;
    }

    setRolling(true);
    const value = engine.rollDice();

    // Show rolling animation, then execute move
    setTimeout(() => {
      setDiceValue(value);
      setRolling(false);
      executeMove(value);
    }, 800);
  }, [rolling, animating, settings]);

  const executeMove = useCallback(
    (value: number) => {
      const engine = engineRef.current;

      try {
        const result = engine.executeMove(value);
        setLastMove(result);
        setAnimating(true);

        // Show notification for snake/ladder
        if (result.snakeOrLadder) {
          const entity = result.snakeOrLadder;
          addNotification({
            type: entity.type === 'snake' ? NotificationType.WARNING : NotificationType.SUCCESS,
            title: entity.type === 'snake' ? '🐍 Snake!' : '🪜 Ladder!',
            message:
              entity.type === 'snake'
                ? `${result.player} slid down from ${result.toPosition} to ${result.finalPosition}!`
                : `${result.player} climbed from ${result.toPosition} to ${result.finalPosition}!`,
            duration: 2500,
          });
        }

        if (result.bouncedBack) {
          addNotification({
            type: NotificationType.INFO,
            title: 'Bounce Back!',
            message: `Overshot 100! Bounced back to ${result.finalPosition}.`,
            duration: 2000,
          });
        }

        if (result.forfeitedTurn) {
          addNotification({
            type: NotificationType.WARNING,
            title: 'Three 6s!',
            message: `${result.player} rolled three 6s in a row — turn forfeited!`,
            duration: 3000,
          });
        }

        // After animation, advance turn
        setTimeout(() => {
          setAnimating(false);

          if (result.won) {
            handleGameOver(result);
            return;
          }

          // Advance turn
          engine.advanceTurn();
          const newState = engine.getGameState();
          setGameState(newState);

          // Auto-save
          if (persistenceRef.current && settings) {
            persistenceRef.current.autoSave(settings.mode, newState, {
              aiDifficulty: settings.aiDifficulty,
              startedAt: newState.startedAt,
              lastSavedAt: Date.now(),
            });
          }

          // If next player is AI, schedule auto-roll
          const nextPlayer = engine.getCurrentPlayer();
          if (nextPlayer.type === 'ai' && aiRef.current && !engine.isGameOver()) {
            aiRef.current.scheduleRoll(() => {
              handleAIRoll();
            });
          }
        }, 600);
      } catch (error) {
        setAnimating(false);
        errorHandlerRef.current?.handleGameLogicError(
          error instanceof Error ? error.message : 'Move execution failed'
        );
      }
    },
    [addNotification, settings]
  );

  const handleAIRoll = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver() || rolling) return;

    const value = engine.rollDice();
    setDiceValue(value);
    setRolling(true);

    setTimeout(() => {
      setRolling(false);
      executeMove(value);
    }, 500);
  }, [rolling, executeMove]);

  const handleGameOver = useCallback(
    (result: MoveResult) => {
      const engine = engineRef.current;
      const state = engine.getGameState();
      setGameState(state);
      setView('game-over');

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      if (statsServiceRef.current && settings) {
        const snakesHit = state.moveHistory.filter(
          (m) => m.snakeOrLadder?.type === 'snake'
        ).length;
        const laddersClimbed = state.moveHistory.filter(
          (m) => m.snakeOrLadder?.type === 'ladder'
        ).length;

        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            mode: settings.mode,
            difficulty: settings.aiDifficulty,
            winner: result.player,
            totalMoves: state.moveHistory.length,
            snakesHit,
            laddersClimbed,
            duration: Math.floor((Date.now() - state.startedAt) / 1000),
            completedAt: Date.now(),
          },
          result.player,
          isAuthenticated ? user?.id : undefined
        );
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: '🎉 Game Over!',
        message: `${result.player} wins the game!`,
        duration: 5000,
      });
    },
    [addNotification, settings, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    // Leave multiplayer room if active
    if (roomCode) {
      multiplayerService.leaveGame();
    }
    aiRef.current?.destroy();
    aiRef.current = null;
    setView('mode-select');
    setGameState(null);
    setDiceValue(null);
    setLastMove(null);
    setSettings(null);
    setRoomCode(null);
    setWaitingForOpponent(false);
    setOpponentConnected(false);
    setConnectionStatus('connected');
    isCreatingRoom.current = false;
  }, [roomCode, multiplayerService]);

  const handleExit = useCallback(() => {
    // End game session if active
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    onExit?.();
  }, [onExit]);

  // =========================================================================
  // Trigger AI on initial turn if AI goes first (shouldn't happen, but safe)
  // =========================================================================
  useEffect(() => {
    if (view !== 'playing' || !gameState || !aiRef.current) return;
    const engine = engineRef.current;
    const currentPlayer = engine.getCurrentPlayer();
    if (currentPlayer.type === 'ai' && !engine.isGameOver() && !rolling && !animating) {
      aiRef.current.scheduleRoll(() => handleAIRoll());
    }
    // Only run when view changes to playing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const slConfig: GameModeConfig = {
      gameName: 'Snakes & Ladders',
      gameIcon: '🐍',
      subtitle: 'Classic board game',
      availableModes: ['ai', 'local', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: (selection.difficulty as any) || 'medium',
          playerCount: 2,
          extraTurnOnSix: true,
        });
      } else if (selection.mode === 'local') {
        handleStartGame({
          mode: 'local-multiplayer',
          playerCount: 2,
          extraTurnOnSix: true,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime', playerCount: 2, extraTurnOnSix: true });
      }
    };

    return (
      <div className="sl-game">
        <div className="sl-game__header">
          <h2 className="sl-game__title">🐍 Snakes & Ladders</h2>
        </div>
        <UniversalGameModeSelector
          config={slConfig}
          onStart={handleModeSelection}
          onJoinRoom={handleJoinPrivateGame}
          onCreateRoom={() => handleStartGame({ mode: 'multiplayer-realtime', playerCount: 2, extraTurnOnSix: true })}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!gameState) {
    // Show waiting room if we're in multiplayer and waiting for opponent
    if (waitingForOpponent && roomCode) {
      return (
        <div className="sl-game">
          <RoomCodeDisplay
            roomCode={roomCode}
            gameType="checkers"
            playerName="Guest"
            maxPlayers={4}
            onCancel={handleCancelWaiting}
            showCancelButton={true}
          />
        </div>
      );
    }
    return null;
  }

  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const isHumanTurn = currentPlayer.type === 'human';
  const isDiceDisabled = !isHumanTurn || rolling || animating || gameState.gameStatus === 'completed';

  // Compute game stats for game-over screen
  const snakesHit = gameState.moveHistory.filter(m => m.snakeOrLadder?.type === 'snake').length;
  const laddersClimbed = gameState.moveHistory.filter(m => m.snakeOrLadder?.type === 'ladder').length;

  // Game over config
  const gameOverConfig = (view === 'game-over' && gameState.winner) ? {
    show: true,
    result: 'win' as 'win' | 'loss' | 'draw',
    title: '🎉 Game Over!',
    subtitle: `${gameState.winner} wins! (${gameState.moveHistory.length} moves, ${snakesHit} snakes, ${laddersClimbed} ladders)`,
    icon: '🎉',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Snakes & Ladders',
    board: (
      <div className="sl-game__board-area">
        <SnakesLaddersBoard
          players={gameState.players}
          currentPlayerIndex={gameState.currentPlayerIndex}
          lastMove={lastMove ? {
            fromPosition: lastMove.fromPosition,
            toPosition: lastMove.toPosition,
            finalPosition: lastMove.finalPosition,
          } : null}
          animating={animating}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'players',
        title: 'Players',
        icon: '👥',
        content: (
          <div className="sl-game__player-info" role="status" aria-live="polite">
            {/* Player positions */}
            <div className="sl-game__positions">
              {gameState.players.map((p) => (
                <div
                  key={p.id}
                  className={`sl-game__player-row ${p.color === currentPlayer.color ? 'sl-game__player-row--active' : ''}`}
                >
                  <span className={`sl-game__player-dot sl-game__player-dot--${p.color}`} aria-hidden="true" />
                  <span className="sl-game__player-name">
                    {p.type === 'ai' ? `AI (${p.color})` : p.color}
                  </span>
                  <span className="sl-game__player-pos">Pos: {p.position}</span>
                </div>
              ))}
            </div>

            {/* Last move info */}
            {lastMove && (
              <div className="sl-game__last-move" aria-live="polite">
                <p>
                  <strong>{lastMove.player}</strong> rolled {lastMove.diceValue}
                  {lastMove.bouncedBack && ' (bounced back!)'}
                  {lastMove.snakeOrLadder && ` → ${lastMove.snakeOrLadder.type === 'snake' ? '🐍' : '🪜'} to ${lastMove.finalPosition}`}
                  {lastMove.extraTurn && ' — Extra turn!'}
                  {lastMove.forfeitedTurn && ' — Turn forfeited (3 sixes)!'}
                </p>
              </div>
            )}

            {/* Extra turn indicator */}
            {gameState.consecutiveSixes > 0 && gameState.extraTurnEnabled && (
              <div className="sl-game__extra-turn-warning" role="alert">
                ⚠️ Consecutive 6s: {gameState.consecutiveSixes}/3
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'dice',
        title: 'Dice',
        icon: '🎲',
        content: (
          <SnakesLaddersDice
            value={diceValue}
            rolling={rolling}
            disabled={isDiceDisabled}
            onRoll={handleRollDice}
            playerColor={currentPlayer.color}
          />
        ),
      },
    ],
    statusBar: {
      turnIndicator: (
        <span>
          <span className={`sl-game__player-dot sl-game__player-dot--${currentPlayer.color}`} aria-hidden="true" />
          {' '}
          {currentPlayer.type === 'ai'
            ? `AI (${currentPlayer.color}) is thinking...`
            : `${currentPlayer.color}'s turn`}
        </span>
      ),
      gameStatus: gameState.gameStatus === 'completed' ? 'Game Over' : undefined,
      gameStatusType: gameState.gameStatus === 'completed' ? 'ended' : 'active',
      aiThinking: currentPlayer.type === 'ai',
      connectionStatus: settings?.mode === 'multiplayer-realtime' ? connectionStatus : undefined,
      roomCode: roomCode || undefined,
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="sl-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const SnakesLaddersGame: React.FC<SnakesLaddersGameProps> = (props) => (
  <ErrorBoundary componentName="SnakesAndLadders">
    <SnakesLaddersGameInner {...props} />
  </ErrorBoundary>
);

export { SnakesLaddersGame };
export default SnakesLaddersGame;
